import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import getTourListingsGoogleMapsDirectionsUrl from 'common/util/getTourListingsGoogleMapsDirectionsUrl';

import {
  LISTING_DATA_SOURCE_SMART_APARTMENT,
} from 'common/listingConstants';

import {
  TOUR_SHOWING_STATUS_FAILED,
  TOUR_TYPE_SELF_SHOWING,
} from 'common/tourConstants';

import {
  getTourShowingLabel,
  getTourShowingUserRatingLabel,
} from 'common/tourHelpers';

import {
  getApplicationMovePeriodFormat,
} from 'common/householdApplication/applicationHelpers';

import {
  SR_API_ACTION_ADMIN_REQUEST_TOUR_SHOWING,
  SR_API_ACTION_LISTING_WANTS_TO_APPLY,
} from 'api/actionTypes';

import {
  isTourUserPrequalCompleted,
} from 'client/components/SrAdminTourCard/tourCardHelpers';
import SrLink from 'client/components/SrLink/SrLink';
import SrTourCopyMlsNumber from 'client/components/SrTourCopyMlsNumber';
import SrTourCopyAllMlsNumbersHeader from 'client/components/SrTourCopyAllMlsNumbersHeader';
import SrTourCopyAllAddressesHeader from 'client/components/SrTourCopyAllAddressesHeader';
import SrAdminTourNotes from 'client/components/SrAdminTourCard/SrAdminTourNotes';
import SrAdminTourCardTourReportRow from 'client/components/SrAdminTourCard/SrAdminTourCardTourReportRow';
import SrAssignHouseholdAdminSelect from 'client/components/SrAssignHouseholdAdminSelect';
import SrAdminTourCardListingCell from 'client/components/SrAdminTourCard/SrAdminTourCardListingCell';
import SrHouseholdNotesContainer from 'client/components/SrHouseholdNotesContainer';
import SrBadge from 'client/components/SrBadge';
import SrCopyText from 'client/components/SrCopyText';
import SrFrontConversation from 'client/components/SrFrontConversation';

import branch from 'client/core/branch';
import postWithState from 'client/actions/postWithState';

@branch({
  householdAdminsList: ['householdAdminsList'],
})
class SrAdminTourCardCompleted extends Component {
  static propTypes = {
    tour: PropTypes.object.isRequired,
    onNotAvailableShowingButtonClick: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    appUrl: PropTypes.string.isRequired,
    householdAdminsList: PropTypes.array,
    onOpenRenterPrequal: PropTypes.func.isRequired,
    reloadDataTableTour: PropTypes.func.isRequired,
  }

  _onShowingCreateNewTourRequestButtonClick = ({ listingId }) => {
    const { tour, reloadDataTableTour, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_REQUEST_TOUR_SHOWING,
      queryId: `${tour.id}-${listingId}`,
      payload: {
        userId: tour.userId,
        listingId: listingId,
      },
    }).then((tourWithUpdatedShowing) => {
      // (Re)load tour holding the newly requested showing, created by re-scheduling this showing.
      return reloadDataTableTour({
        tourIdToLoad: tourWithUpdatedShowing.id,
      });
    }).then(() => {
      if (typeof global.alert === 'function') {
        global.alert('Showing has been re-scheduled successfully.');
      }
    });
  }

  _onShowingWantsToApplyButtonClick = ({ listingId, wantsToApply }) => {
    const { tour, reloadDataTableTour, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_LISTING_WANTS_TO_APPLY,
      queryId: `${tour.userId}-${listingId}`,
      payload: {
        userId: tour.userId,
        listingId: listingId,
        wantsToApply: wantsToApply,
      },
    }).then(reloadDataTableTour);
  }

  render() {
    const {
      tour,
      onNotAvailableShowingButtonClick,
      appUrl,
      reloadDataTableTour,
      householdAdminsList,
      onOpenRenterPrequal,
      dispatch,
    } = this.props;

    const {
      user,
      renterProfile,
    } = tour;

    const isPrequalified = isTourUserPrequalCompleted(tour);
    const userFrontConversation = tour.userFrontConversations.find(({ userId }) => userId === user.id);
    const agentUser = tour.agentUser;
    const householdAssignedAdminUserId = tour.household && tour.household.assignedAdminUserId;
    const googleMapsDirectionsUrl = getTourListingsGoogleMapsDirectionsUrl({
      listings: tour.listings,
      showings: tour.showings,
    });

    return (
      <div className={`tourCard completed renter-${user.phone}`}>
        {/* Top info bar */}
        <div className='topInfo'>
          <div>
            <div>
              <span className='renterNameInfo'>
                <span
                  onClick={() => onOpenRenterPrequal(user.id)}
                  className={classnames('renterName', {
                    isPrequalified: isPrequalified,
                  })}
                >
                  {user.name}
                </span>
              </span>
              <SrBadge>{tour.market && tour.market.displayName}</SrBadge>
            </div>
            <div className='subhead'>
              {userFrontConversation && (
                <SrFrontConversation
                  frontConversation={userFrontConversation}
                  user={user}
                  dispatch={dispatch}
                />
              )}
              {userFrontConversation && (
                <SrLink
                  className='linkOut front'
                  target='_blank'
                  to={`https://app.frontapp.com/open/${userFrontConversation.conversationId}`}
                >
                  Front
                </SrLink>
              )}
              {googleMapsDirectionsUrl && (
                <SrLink
                  className='linkOut gmaps'
                  target='_blank'
                  to={googleMapsDirectionsUrl}
                >
                  Maps
                </SrLink>
              )}
              <SrLink
                className='linkOut household'
                target='_blank'
                to={`${appUrl}/admin/household/all/${user.id}`}
              >
                Household
              </SrLink>
              <div className='subhead'>
                <span className='detail'>
                  {renterProfile
                    ? getApplicationMovePeriodFormat(renterProfile)
                    : `${momentWithAustinTimezone(tour.renterMoveInDate).format('MM/DD/YYYY')} Move-in Date`
                  }
                </span>
                <span className='detail additional'>
                  <SrCopyText
                    formattedText={`Phone ${user.phone}`}
                    text={user.phone}
                  />
                </span>
                <span className='detail additional'>
                  {tour.chosenDates.map((timeDate, i) => {
                    const format = i === 0 ? 'MMM Do: h:mma' : 'h:mma';
                    return momentWithAustinTimezone(timeDate).format(format);
                  }).join(', ')}
                </span>
              </div>
            </div>
          </div>
          <div className='timestampContainer'>
            <span className='timestamp'>
              {tour.type === TOUR_TYPE_SELF_SHOWING ? 'Self Tour | ' : ''}
              {agentUser
                ? (
                  `${agentUser.name} completed at ` +
                  moment(tour.lastStatusUpdateDate).format('h:mmA [on] MM/DD/YYYY')
                )
                : `Completed at ${moment(tour.lastStatusUpdateDate).format('h:mmA [on] MM/DD/YYYY')}`
              }
            </span>
          </div>
        </div>

        {/* Table listing info section */}
        <div className='listingTable'>
          <table>
            <thead>
              <tr>
                <th className='listing'>
                  <SrTourCopyAllAddressesHeader
                    user={user}
                    listings={tour.listings}
                    showings={tour.showings}
                  />
                </th>
                <th className='status'>status</th>
                <th className='availableDate'>availability</th>
                <th className='mls'>
                  <SrTourCopyAllMlsNumbersHeader
                    listings={tour.listings}
                    showings={tour.showings}
                  />
                </th>
                <th className='tourOutcome'>result</th>
                <th className='notes'>notes</th>
                <th className='agent'>review</th>
                <th className='actions'>listing actions</th>
              </tr>
            </thead>
            <tbody>
              {tour.listings.map((listing) => {
                const showing = tour.showings.find(({ listingId }) => listingId === listing.id);
                const showingRating = getTourShowingUserRatingLabel(showing.userRating);

                return (
                  <tr
                    key={`${tour.id}-${listing.id}`}
                    className={classnames({
                      notAvailable: showing.status === TOUR_SHOWING_STATUS_FAILED,
                    })}
                  >
                    <SrAdminTourCardListingCell listing={listing} showing={showing} />
                    <td className='status'>
                      {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
                        ? listing.status
                        : listing.mlsStatus
                      }
                    </td>
                    <td className='availableDate'>
                      {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
                        ? (
                          <SrLink
                            to={
                              'https://app.smartapartmentdata.com/ViewProperty.aspx' +
                              `?propertyID=${encodeURIComponent(listing.smartApartmentId)}`
                            }
                            target='_blank'
                          >
                            View Floorplans
                          </SrLink>
                        )
                        : listing.availableDate
                          ? moment.utc(listing.availableDate).format('MMM Do, YYYY')
                          : '--'
                      }
                    </td>
                    <td className='mls'>
                      {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
                        ? ('Smart')
                        : (
                          <SrTourCopyMlsNumber mlsNumber={listing.mlsNumber} />
                        ) || '--'
                      }
                    </td>
                    <td className='tourOutcome'>
                      {showing.note && (
                        <span className='tooltip'>
                          {showing.note}
                        </span>
                      )}
                      {getTourShowingLabel({ tourShowingStatus: showing.status, hasNote: showing.note })}
                    </td>
                    <SrAdminTourNotes
                      showing={showing}
                      listing={listing}
                    />
                    <td className='agent'>
                      {showingRating}
                    </td>
                    <td className='actions'>
                      <div
                        className={classnames(
                          'listingActionButton',
                          (showing.wantsToApply)
                          && 'confirmed',
                        )}
                      >
                        <div className='confirmContainer'>
                          <span
                            className='mainAction'
                            onClick={() => this._onShowingWantsToApplyButtonClick({
                              listingId: listing.id,
                              wantsToApply: !showing.wantsToApply,
                            })}
                          >
                            wants to apply
                          </span>
                          <div className='arrowDropDown' />
                          <div className='dropdown'>
                            <span
                              className='option reschedule'
                              onClick={() => this._onShowingCreateNewTourRequestButtonClick({
                                listingId: listing.id,
                              })}
                            >
                              create new tour request
                            </span>
                            <span
                              className='option delete'
                              onClick={() => onNotAvailableShowingButtonClick({
                                listingId: listing.id,
                              })}
                            >
                              not available
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className='notesContainer'>
          <span className='notesTitle'>
            notes
          </span>

          <SrHouseholdNotesContainer
            household={tour.household}
            reloadDataTable={reloadDataTableTour}
          />
        </div>
        <SrAdminTourCardTourReportRow
          dispatch={dispatch}
          tour={tour}
          reloadDataTableTour={reloadDataTableTour}
        />
        {tour.household
          && householdAdminsList
          && (
            <div className='bottomActions clearfix'>
              <SrAssignHouseholdAdminSelect
                householdId={tour.household.id}
                assignedAdminUserId={householdAssignedAdminUserId}
                reloadDataTable={reloadDataTableTour}
                householdAdminsList={householdAdminsList}
                dispatch={dispatch}
              />
            </div>
          )
        }
      </div>
    );
  }
}

SrAdminTourCardCompleted.displayName = 'SrAdminTourCardCompleted';

export default SrAdminTourCardCompleted;
